import {
  chatLibraryFileControllerAttachFile,
  chatLibraryFileControllerDetachFile,
  fileControllerGetFiles,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";

export const useChatFileLibraryFileData = ({
  chatUuid,
}: {
  chatUuid: string | null;
}) => {
  const queryClient = useQueryClient();
  const enabled = useIsApiReady() && !!chatUuid;

  const { data, isLoading } = useQuery({
    enabled,
    queryKey: queryKeys.files({
      chatUuid: chatUuid!,
      isLibrary: true,
    }),
    queryFn: () =>
      fileControllerGetFiles({
        query: {
          filter: {
            chatUuid: chatUuid!,
            isLibrary: true,
          },
        },
      }).then((res) => res.data?.data ?? []),
  });

  const invalidate = () => {
    if (!chatUuid) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.files({
        chatUuid: chatUuid!,
        isLibrary: true,
      }),
    });
  };

  const attach = async (fileUuid: string): Promise<void> => {
    if (!chatUuid) {
      throw new Error("chatUuid is required");
    }
    await chatLibraryFileControllerAttachFile({
      path: { fileUuid, chatUuid },
      throwOnError: true,
    });
    invalidate();
  };

  const detach = async (fileUuid: string): Promise<void> => {
    if (!chatUuid) {
      throw new Error("groupUuid, kitUuid, and chatUuid are required");
    }
    queryClient.setQueryData(
      queryKeys.files({
        chatUuid: chatUuid!,
        isLibrary: true,
      }),
      (prev: typeof data) => prev?.filter((f) => f.uuid !== fileUuid),
    );
    try {
      await chatLibraryFileControllerDetachFile({
        path: { chatUuid, fileUuid },
        throwOnError: true,
      });
    } catch {
      invalidate();
    }
  };

  return {
    chatFiles: data ?? [],
    isLoading,
    attach,
    detach,
  };
};
